import { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { getFirebaseDb } from '../config/firebase';
import { useAuth } from './AuthContext';
import { db } from '../config/firebase';
// RBAC removed - roleUtils deleted

const defaultColors = {
  primary: '#4d8063',
  secondary: '#1F1F1F'
};

const ThemeContext = createContext({
  isDarkMode: false,
  toggleTheme: () => {},
  primaryColor: defaultColors.primary,
  secondaryColor: defaultColors.secondary,
  setPrimaryColor: () => {},
  setSecondaryColor: () => {},
  isAdmin: false,
});

// Add this function at the top of the ThemeProvider component
const clearInvalidThemeData = () => {
  try {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === null) return;
    
    // Try parsing it to validate
    JSON.parse(savedTheme);
  } catch (error) {
    // If parsing fails, remove the invalid data
    localStorage.removeItem('theme');
    console.warn('Cleared invalid theme data from localStorage');
  }
};

// Helper function to convert hex to HSL for CSS variables
const hexToHSL = (hex) => {
  // Remove the # if present
  hex = hex.replace('#', '');
  
  // Convert the hex values to RGB
  let r = parseInt(hex.substring(0, 2), 16) / 255;
  let g = parseInt(hex.substring(2, 4), 16) / 255;
  let b = parseInt(hex.substring(4, 6), 16) / 255;
  
  // Find the minimum and maximum values to calculate the lightness
  let max = Math.max(r, g, b);
  let min = Math.min(r, g, b);
  let l = (max + min) / 2;
  let s = 0;
  let h = 0;
  
  // Calculate the saturation
  if (max !== min) {
    s = l > 0.5 ? (max - min) / (2 - max - min) : (max - min) / (max + min);
    
    // Calculate the hue
    if (max === r) {
      h = (g - b) / (max - min) + (g < b ? 6 : 0);
    } else if (max === g) {
      h = (b - r) / (max - min) + 2;
    } else if (max === b) {
      h = (r - g) / (max - min) + 4;
    }
    h = h * 60;
  }
  
  // Round the values
  h = Math.round(h);
  s = Math.round(s * 100);
  l = Math.round(l * 100);
  
  return { h, s, l };
};

export function ThemeProvider({ children }) {
  const auth = useAuth();
  const currentUser = auth?.currentUser;
  const [isAdmin, setIsAdmin] = useState(false);

  // Initialize theme state with a default value
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Handle localStorage initialization in a separate effect
  useEffect(() => {
    const initializeTheme = () => {
      try {
        const savedTheme = localStorage.getItem('theme');
        
        // If no theme is saved, set default and return
        if (!savedTheme) {
          localStorage.setItem('theme', JSON.stringify(false));
          return;
        }

        // Try to parse the saved theme
        const parsed = JSON.parse(savedTheme);
        
        // Validate the parsed value
        if (typeof parsed !== 'boolean') {
          localStorage.setItem('theme', JSON.stringify(false));
          return;
        }

        // Only update state if the parsed value is different
        if (parsed !== isDarkMode) {
          setIsDarkMode(parsed);
        }
      } catch (error) {
        // If there's any error, reset to default
        console.warn('Error initializing theme:', error);
        localStorage.setItem('theme', JSON.stringify(false));
      }
    };

    initializeTheme();
  }, []); // Run once on mount

  const [primaryColor, setPrimaryColor] = useState(defaultColors.primary);
  const [secondaryColor, setSecondaryColor] = useState(defaultColors.secondary);

  // RBAC removed - all authenticated users are treated as admin
  useEffect(() => {
    setIsAdmin(!!auth?.userRole);
  }, [auth?.userRole]);

  // Load global brand colors for everyone (staff, tenant portal, contractor portal).
  // Tenant/contractor often have no Firebase auth user — they still need company colours.
  // Requires Firestore: allow read on settings/company for unauthenticated users (see firestore.rules).
  useEffect(() => {
    let isMounted = true;

    const loadGlobalBrandColors = async () => {
      try {
        console.log('🎨 [ThemeContext] Attempting to load brand colors from Firestore...');
        const companySettingsRef = doc(db, 'settings', 'company');
        const companySettingsDoc = await getDoc(companySettingsRef);

        if (!isMounted) {
          console.log('⚠️ [ThemeContext] Component unmounted, skipping color load');
          return;
        }

        if (companySettingsDoc.exists()) {
          const settings = companySettingsDoc.data();
          console.log('✅ [ThemeContext] Brand colors loaded from Firestore:', {
            primaryColor: settings.primaryColor || 'NOT SET',
            secondaryColor: settings.secondaryColor || 'NOT SET',
            hasData: companySettingsDoc.exists()
          });

          // Load brand colors from global settings - always update if they exist
          if (settings.primaryColor) {
            console.log('🎨 [ThemeContext] Setting primary color to:', settings.primaryColor);
            setPrimaryColor(settings.primaryColor);
          } else {
            console.warn('⚠️ [ThemeContext] No primaryColor in settings, using default:', defaultColors.primary);
          }
          if (settings.secondaryColor) {
            console.log('🎨 [ThemeContext] Setting secondary color to:', settings.secondaryColor);
            setSecondaryColor(settings.secondaryColor);
          } else {
            console.warn('⚠️ [ThemeContext] No secondaryColor in settings, using default:', defaultColors.secondary);
          }
        } else {
          console.warn('❌ [ThemeContext] Company settings document does NOT exist in Firestore!');
          console.warn('⚠️ [ThemeContext] This means your colors have never been saved or the document was deleted');
          console.warn('💡 [ThemeContext] Using default colors:', defaultColors);
        }
      } catch (error) {
        if (error.code === 'permission-denied') {
          console.warn(
            '⚠️ [ThemeContext] Permission denied reading settings/company (brand colours). ' +
              'Ensure firestore.rules allows public read on settings/company for tenant/contractor portals.'
          );
          return;
        }
        
        console.error('❌ [ThemeContext] Error loading global brand colors:', error);
        console.error('📋 [ThemeContext] Error details:', {
          message: error.message,
          code: error.code,
          stack: error.stack
        });
        // Don't reset to defaults on error - keep current values
      }
    };

    loadGlobalBrandColors();

    // Set up real-time listener for global brand color changes
    console.log('📡 [ThemeContext] Setting up real-time listener for brand color changes...');
    const companySettingsRef = doc(db, 'settings', 'company');
    const unsubscribe = onSnapshot(companySettingsRef, (docSnapshot) => {
      if (!isMounted) {
        console.log('⚠️ [ThemeContext] Listener fired but component unmounted');
        return;
      }

      if (docSnapshot.exists()) {
        const settings = docSnapshot.data();
        console.log('🔄 [ThemeContext] Brand colors updated via real-time listener:', {
          primaryColor: settings.primaryColor || 'NOT SET',
          secondaryColor: settings.secondaryColor || 'NOT SET'
        });

        // Only update if colors exist in the document
        if (settings.primaryColor) {
          console.log('🎨 [ThemeContext] Updating primary color from listener:', settings.primaryColor);
          setPrimaryColor(settings.primaryColor);
        }
        if (settings.secondaryColor) {
          console.log('🎨 [ThemeContext] Updating secondary color from listener:', settings.secondaryColor);
          setSecondaryColor(settings.secondaryColor);
        }
      } else {
        console.warn('❌ [ThemeContext] Listener: Company settings document does NOT exist');
        console.warn('💡 [ThemeContext] Listener: Colors will remain at current values (not resetting to defaults)');
      }
    }, (error) => {
      if (error.code === 'permission-denied') {
        console.warn('⚠️ [ThemeContext] Brand colour listener: permission denied on settings/company');
        return;
      }
      
      console.error('❌ [ThemeContext] Error in brand colors listener:', error);
      console.error('📋 [ThemeContext] Listener error details:', {
        message: error.message,
        code: error.code
      });
      // Don't reset colors on listener error
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  // Load user preferences (dark mode only - personal preference)
  useEffect(() => {
    const loadUserPreferences = async () => {
      if (!currentUser) return;
      
      try {
        const userPrefsRef = doc(db, 'userPreferences', currentUser.uid);
        const userPrefsDoc = await getDoc(userPrefsRef);
        
        if (userPrefsDoc.exists()) {
          const prefs = userPrefsDoc.data();
          // Only load dark mode preference (brand colors are global)
          setIsDarkMode(prefs?.theme?.isDarkMode ?? false);
        } else {
          // Create default preferences (only dark mode, no brand colors)
          await setDoc(userPrefsRef, {
            theme: {
              isDarkMode: false,
            }
          });
        }
      } catch (error) {
        console.error('Error loading user preferences:', error);
      }
    };

    loadUserPreferences();
  }, [currentUser]);

  // Save user preferences (dark mode only - brand colors are global)
  const saveUserPreferences = async () => {
    if (!currentUser) return;

    try {
      const db = getFirebaseDb();
      const userPrefsRef = doc(db, 'userPreferences', currentUser.uid);
      
      console.log('Saving theme preferences (dark mode only):', {
        isDarkMode
      });
      
      await updateDoc(userPrefsRef, {
        theme: {
          isDarkMode,
          // Brand colors are no longer stored per-user
        }
      });
      
      console.log('Theme preferences saved successfully');
      
    } catch (error) {
      console.error('Error saving user preferences:', error);
    }
  };

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  // Save preferences when dark mode changes (brand colors are global, not saved here)
  useEffect(() => {
    if (currentUser) {
      // Debounce saving preferences to avoid multiple rapid DB writes
      const timeoutId = setTimeout(() => {
        saveUserPreferences();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isDarkMode, currentUser]);

  // Update localStorage when theme changes
  useEffect(() => {
    try {
      localStorage.setItem('theme', JSON.stringify(isDarkMode));
    } catch (error) {
      console.warn('Error saving theme:', error);
    }
  }, [isDarkMode]);

  // Apply theme to root element and update CSS variables
  useEffect(() => {
    const root = window.document.documentElement;
    const body = window.document.body;
    
    console.log('Applying theme CSS variables:', {
      isDarkMode,
      primaryColor,
      secondaryColor
    });
    
    // Toggle dark mode class
    if (isDarkMode) {
      root.classList.add('dark');
      body.style.backgroundColor = '#0F172A';
      body.style.color = '#FFFFFF';
    } else {
      root.classList.remove('dark');
      body.style.backgroundColor = '#FFFFFF';
      body.style.color = '#1F1F1F';
    }

    // Update CSS variables
    root.style.setProperty('--color-primary', primaryColor);
    root.style.setProperty('--primary-color', primaryColor);
    root.style.setProperty('--color-secondary', secondaryColor);
    root.style.setProperty('--secondary-color', secondaryColor);
    
    // Convert colors to HSL for Tailwind/shadcn compatibility
    try {
      if (primaryColor) {
        const primaryHSL = hexToHSL(primaryColor);
        root.style.setProperty('--primary', `${primaryHSL.h} ${primaryHSL.s}% ${primaryHSL.l}%`);
      }
      
      if (secondaryColor) {
        const secondaryHSL = hexToHSL(secondaryColor);
        root.style.setProperty('--secondary', `${secondaryHSL.h} ${secondaryHSL.s}% ${secondaryHSL.l}%`);
      }
    } catch (e) {
      console.warn('Error converting colors to HSL:', e);
    }
    
    // Force a repaint by touching the DOM
    document.body.style.display = 'none';
    void document.body.offsetHeight; // Trigger a reflow
    document.body.style.display = '';
    
  }, [isDarkMode, primaryColor, secondaryColor]);

  // Helper function to adjust color brightness
  const adjustColor = (color, amount) => {
    return '#' + color.replace(/^#/, '').replace(/../g, color => 
      ('0' + Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).substr(-2)
    );
  };

  // Add this at the beginning of your ThemeContext component
  useEffect(() => {
    // RBAC removed - theme settings apply to all users
  }, [auth?.userRole]);

  const value = {
    isDarkMode,
    toggleTheme,
    primaryColor,
    secondaryColor,
    setPrimaryColor,
    setSecondaryColor,
    isAdmin,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
